import { useState, useEffect } from 'react';
import { Title, Button, Text, Stack, Group, Alert, Box, TextInput, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { flowsApi, Flow } from '@/services/flows.api';
import { useAuth } from '@/features/auth/useAuth';
import { getCurrentUserRole } from '@/features/auth/currentUser';
import { OfflineKitBanner } from '@/components/OfflineKit/OfflineKitBanner';
import { useTemplates } from '@/features/dashboard/hooks/useTemplates';
import { FlowCard } from '@/features/dashboard/components/FlowCard';
import { CreateFlowModal } from '@/features/dashboard/components/CreateFlowModal';
import { EditFlowModal } from '@/features/dashboard/components/EditFlowModal';
import { DeleteFlowModal } from '@/features/dashboard/components/DeleteFlowModal';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { FlowFilter } from '@/layouts/DashboardLayout';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { activeFilter } = useOutletContext<{ activeFilter: FlowFilter }>();
  const [createOpened, setCreateOpened] = useState(false);
  const [editOpened, setEditOpened] = useState(false);
  const [deleteOpened, setDeleteOpened] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [search, setSearch] = useState('');
  const { isAuthenticated, isLoading: isAuthenticating, error: authError } = useAuth();

  // Listen for create-flow event from DashboardLayout top bar
  useEffect(() => {
    const handler = () => setCreateOpened(true);
    window.addEventListener('holloway:create-flow', handler);
    return () => window.removeEventListener('holloway:create-flow', handler);
  }, []);

  const { data: flows, error: flowsError, isLoading: flowsLoading } = useQuery({
    queryKey: ['flows'],
    queryFn: () => flowsApi.getFlows(),
    enabled: isAuthenticated,
    retry: false,
  });

  const { templates } = useTemplates(isAuthenticated);

  const { data: trashedFlows, isLoading: trashedLoading } = useQuery({
    queryKey: ['flows-trashed'],
    queryFn: () => flowsApi.getTrashedFlows(),
    enabled: isAuthenticated && activeFilter === 'trash',
    retry: false,
  });

  const handleEdit = (flow: Flow) => {
    setSelectedFlow(flow);
    setEditOpened(true);
  };

  const handleDelete = (flow: Flow) => {
    setSelectedFlow(flow);
    setDeleteOpened(true);
  };

  const showInitialLoading = isAuthenticating || (flowsLoading && !flows) || (activeFilter === 'trash' && trashedLoading && !trashedFlows);

  if (showInitialLoading) {
    return <LoadingSpinner message={isAuthenticating ? 'Authenticating...' : 'Loading dashboard...'} />;
  }

  if (!isAuthenticated) {
    return (
      <Box p="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Authentication Required" color="red">
          <Text>You need to be logged in to access this page.</Text>
          {authError && <Text size="sm" c="dimmed" mt="xs">{authError}</Text>}
        </Alert>
      </Box>
    );
  }

  const sourceFlows = activeFilter === 'trash'
    ? (Array.isArray(trashedFlows) ? trashedFlows : [])
    : (Array.isArray(flows) ? flows : []);

  const filteredFlows = sourceFlows.filter((f) => {
    const matchesSearch = f.name.toLowerCase().includes(search.toLowerCase()) ||
      (f.description || '').toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;

    if (activeFilter === 'trash' || activeFilter === 'all') return true;
    const latestVersion = f.versions?.[0];
    const isPublished = !!f.publishedVersionId;
    const status = latestVersion?.status;

    switch (activeFilter) {
      case 'published': return isPublished || status === 'PUBLISHED';
      case 'draft': return !isPublished && (status === 'DRAFT' || status === 'IN_REVIEW');
      case 'archived': return status === 'ARCHIVED';
      default: return true;
    }
  });

  const totalFlows = Array.isArray(flows) ? flows.length : 0;

  const filterLabels: Record<FlowFilter, string> = {
    all: 'Flows',
    draft: 'Drafts',
    published: 'Published',
    archived: 'Archived',
    trash: 'Trash',
  };

  return (
    <Box p="xl" px={32}>
      <Stack gap="xl">
        {flowsError && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            Failed to load flows. Make sure the API is running.
          </Alert>
        )}

        <OfflineKitBanner isAdmin={getCurrentUserRole() === 'ADMIN'} />

        {/* Page header */}
        <Box>
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title order={2} c="#2A3140" mb={4}>{filterLabels[activeFilter]}</Title>
              <Text size="sm" c="#4E5A6B" maw={480}>
                Welcome back to your studio. Manage your active data pipelines and
                customer journey architectures from this central canvas.
              </Text>
            </Box>
            <TextInput
              placeholder="Search flows..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              styles={{
                input: { backgroundColor: '#fff', borderColor: '#E4E7ED', width: 220 },
              }}
              radius="md"
            />
          </Group>
        </Box>

        {/* Stats row */}
        <Group gap="md">
          <Box
            px="lg"
            py="md"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #E4E7ED',
              borderRadius: 12,
              flex: 1,
            }}
          >
            <Text size="xs" c="#4E5A6B" tt="uppercase" fw={500} mb={4}>Active Flows</Text>
            <Text size="xl" fw={700} c="#2A3140">{totalFlows}</Text>
          </Box>
          {/* Templates are picked in Create New Flow and managed in the Library,
              so the dashboard keeps only the count, linking to that page. */}
          <UnstyledButton
            onClick={() => navigate('/library/templates')}
            aria-label="Templates, open the template library"
            px="lg"
            py="md"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #E4E7ED',
              borderRadius: 12,
              flex: 1,
            }}
          >
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="#4E5A6B" tt="uppercase" fw={500}>Templates</Text>
              <Text size="xs" c="hollowayPrimary.6" fw={500}>View all →</Text>
            </Group>
            <Text size="xl" fw={700} c="#2A3140">{templates.length}</Text>
          </UnstyledButton>
        </Group>

        {/* Flows table */}
        <Box
          style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            border: '1px solid #E4E7ED',
            overflow: 'hidden',
          }}
        >
          {/* Table header */}
          {filteredFlows.length > 0 && (
            <Box
              px="lg"
              py="sm"
              style={{ borderBottom: '1px solid #E4E7ED' }}
            >
              <Group justify="space-between" align="center" wrap="nowrap">
                <Box style={{ flex: 2, minWidth: 0 }}>
                  <Text size="xs" fw={600} c="#4E5A6B" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Flow Name</Text>
                </Box>
                <Box style={{ flex: 1, minWidth: 120 }}>
                  <Text size="xs" fw={600} c="#4E5A6B" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Last Updated</Text>
                </Box>
                <Box style={{ width: 100 }}>
                  <Text size="xs" fw={600} c="#4E5A6B" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Version</Text>
                </Box>
                <Box style={{ width: 100 }}>
                  <Text size="xs" fw={600} c="#4E5A6B" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Status</Text>
                </Box>
                <Box style={{ width: 100 }}>
                  <Text size="xs" fw={600} c="#4E5A6B" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Actions</Text>
                </Box>
              </Group>
            </Box>
          )}

          {/* Rows */}
          <Stack gap={0}>
            {filteredFlows.length === 0 && (
              <Box p="xl" style={{ textAlign: 'center' }}>
                <Text c="dimmed">
                  {search
                    ? 'No flows match your search.'
                    : activeFilter === 'trash'
                    ? 'Trash is empty.'
                    : activeFilter === 'archived'
                    ? 'No archived flows.'
                    : 'No flows yet. Create one to get started!'}
                </Text>
              </Box>
            )}
            {filteredFlows.map((flow) => (
              <FlowCard
                key={flow.id}
                flow={flow}
                activeFilter={activeFilter}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </Stack>

          {/* Pagination footer */}
          {filteredFlows.length > 0 && (
            <Box
              px="lg"
              py="sm"
              style={{ borderTop: '1px solid #E4E7ED' }}
            >
              <Group justify="space-between">
                <Text size="xs" c="#4E5A6B">
                  Showing 1 to {filteredFlows.length} of {activeFilter === 'trash' ? filteredFlows.length : totalFlows} flows
                </Text>
              </Group>
            </Box>
          )}
        </Box>

        {!flowsLoading && !Array.isArray(flows) && flows && (
          <Alert color="red">
            <Text>Unexpected response format: {JSON.stringify(flows)}</Text>
          </Alert>
        )}
      </Stack>

      <CreateFlowModal opened={createOpened} onClose={() => setCreateOpened(false)} />
      <EditFlowModal opened={editOpened} onClose={() => setEditOpened(false)} flow={selectedFlow} />
      <DeleteFlowModal opened={deleteOpened} onClose={() => setDeleteOpened(false)} flow={selectedFlow} />
    </Box>
  );
}
